import { Show } from "../types/Show";
import apiRetryService, { DEFAULT_RETRY_CONFIG } from "./apiRetryService";
import requestDeduplicationService from "./requestDeduplicationService";
import { debugError, debugLog } from "../utils/debugLog";

interface ScheduleApiResponse {
  success: boolean;
  data: Show[];
  error?: string;
}

const SCHEDULE_API_URL = "https://api.eruptionradio.uk/schedule";

export class ScheduleApiService {
  // Helper function to generate show_id from show name and date/time
  private static generateShowId(showName: string, dateStartTime: string): string {
    // Extract date and time from date_start_time format: "YYYY-MM-DD#HH:MM:SS"
    const [datePart, timePart] = dateStartTime.split('#');
    const timeHour = timePart ? timePart.split(':')[0] + '00' : '1400'; // Default to 1400 if no time
    
    const cleanShowName = showName.toLowerCase()
      .replace(/[^a-z0-9\s&]/g, '')
      .replace(/\s+/g, '-')
      .replace(/&/g, 'and');
    
    return `${cleanShowName}-${datePart}-${timeHour}`;
  }

  static async getSchedule(): Promise<ScheduleApiResponse> {
    const queryKey = ['schedule'];
    
    // Check if request should be deduplicated
    if (requestDeduplicationService.shouldDeduplicate(queryKey, 'schedule-data')) {
      debugLog('🔄 Schedule request deduplicated');
    }
    
    const startTime = Date.now();
    
    try {
      const response = await apiRetryService.executeWithRetry(
        async () => {
          const response = await fetch(SCHEDULE_API_URL, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          return response;
        },
        {
          ...DEFAULT_RETRY_CONFIG,
          maxAttempts: 3, // Critical API, allow more retries
          baseDelay: 1000,
        }
      );

      const rawData = await response.json();

      // The API returns: { schedule: [{ day, date, shows: [...] }] }
      if (!rawData.schedule || !Array.isArray(rawData.schedule)) {
        debugError("📅 Invalid API response structure:", rawData);
        return {
          success: false,
          data: [],
          error: "Invalid API response structure",
        };
      }

      // Flatten all shows from all days into a single array
      const allShows: Show[] = [];

      rawData.schedule.forEach((daySchedule: any) => {
        const { date, shows } = daySchedule;

        if (Array.isArray(shows)) {
          shows.forEach((showData: any) => {
            // Debug: Log the raw show data to see what fields are available
            if (shows.indexOf(showData) === 0) {
            }

            // Transform each show to match our Show interface
            const showName = showData.show || "Unknown Show";
            const dateStartTime = `${date}#${showData.start_time || "00:00:00"}`;
            
            const presenterId = showData.presenter?.id ?? showData.presenter?.numeric_id;
            const transformedShow: Show = {
              show: showName,
              show_id: showData.show_id || ScheduleApiService.generateShowId(showName, dateStartTime),
              // Convert date + start_time to our expected format: YYYY-MM-DD#HH:MM:SS
              date_start_time: dateStartTime,
              // Convert date + end_time to our expected format: YYYY-MM-DD#HH:MM:SS
              date_end_time: showData.end_time
                ? `${date}#${showData.end_time}`
                : undefined,
              images: showData.images || {
                "600x600": "",
                "300x300": "",
                "150x150": "",
              },
              mixcloud_url: "", // Not provided in schedule API
              numeric_presenter_id: presenterId,
              presenter: {
                id: showData.presenter?.id,
                numeric_id: showData.presenter?.numeric_id,
                numeric_presenter_id: presenterId,
                name: showData.presenter?.name || "Unknown Presenter",
                genres: showData.presenter?.genres || "Unknown",
              },
              isLive: false, // Schedule shows are not live by default
              // Sub-title for show episodes
              sub_title: showData.sub_title || undefined,
            };

            // Debug: Log the transformed show to verify end time is included
            if (shows.indexOf(showData) === 0) {
            }

            allShows.push(transformedShow);
          });
        }
      });

      // Record successful request performance
      const responseTime = Date.now() - startTime;
      requestDeduplicationService.recordCacheHit(responseTime);

      return {
        success: true,
        data: allShows,
      };
    } catch (error) {
      // Record failed request performance
      const responseTime = Date.now() - startTime;
      requestDeduplicationService.recordCacheMiss(responseTime);
      
      debugError("❌ Error fetching schedule:", error);
      return {
        success: false,
        data: [],
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  // Get today's remaining shows (for "Up Next" section)
  static async getTodaysUpcomingShows(): Promise<ScheduleApiResponse> {
    try {
      const scheduleResponse = await this.getSchedule();

      if (!scheduleResponse.success) {
        return scheduleResponse;
      }

      const now = new Date();
      const today = now.toISOString().split("T")[0]; // YYYY-MM-DD format
      const currentTime = now.getHours() * 60 + now.getMinutes(); // Minutes since midnight

      // Filter for today's shows that haven't started yet
      const upcomingShows = scheduleResponse.data.filter((show) => {
        // Check if show is today
        const [showDate] = show.date_start_time.split("#");
        if (showDate !== today) {
          return false;
        }

        // Parse the show start time
        const [, timeStr] = show.date_start_time.split("#");
        const [hours, minutes] = timeStr.split(":").map(Number);
        const showTimeMinutes = hours * 60 + minutes;

        // Show is upcoming if it starts later than now
        return showTimeMinutes > currentTime;
      });

      // Sort by start time (earliest first)
      upcomingShows.sort((a, b) =>
        a.date_start_time.localeCompare(b.date_start_time)
      );

      return {
        success: true,
        data: upcomingShows,
      };
    } catch (error) {
      debugError("❌ Error fetching today's upcoming shows:", error);
      return {
        success: false,
        data: [],
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  static async getShowsForDate(date: string): Promise<ScheduleApiResponse> {
    const queryKey = ['schedule', date];
    
    // Check if request should be deduplicated
    if (requestDeduplicationService.shouldDeduplicate(queryKey, 'schedule-date')) {
      debugLog(`🔄 Schedule request for ${date} deduplicated`);
    }
    
    const startTime = Date.now();
    
    try {
      const response = await apiRetryService.executeWithRetry(
        async () => {
          const response = await fetch(`${SCHEDULE_API_URL}/${date}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          return response;
        },
        {
          ...DEFAULT_RETRY_CONFIG,
          maxAttempts: 3,
          baseDelay: 1000,
        }
      );

      const data = await response.json();

      // Transform the data to match our Show interface
      const shows: Show[] = [];
      if (data.shows && Array.isArray(data.shows)) {
        data.shows.forEach((showData: any) => {
          const showName = showData.show || "Unknown Show";
          const dateStartTime = `${date}#${showData.start_time || "00:00:00"}`;
          
          const transformedShow: Show = {
            show: showName,
            show_id: showData.show_id || ScheduleApiService.generateShowId(showName, dateStartTime),
            date_start_time: dateStartTime,
            date_end_time: showData.end_time
              ? `${date}#${showData.end_time}`
              : undefined,
            images: showData.images || {
              "600x600": "",
              "300x300": "",
              "150x150": "",
            },
            mixcloud_url: showData.mixcloud_url || "",
            presenter: {
              name: showData.presenter?.name || "Unknown Presenter",
              genres: showData.presenter?.mixcloud_genres || "Unknown",
            },
            isLive: false,
            // Sub-title for show episodes
            sub_title: showData.sub_title || undefined,
          };
          shows.push(transformedShow);
        });
      }

      // Record successful request performance
      const responseTime = Date.now() - startTime;
      requestDeduplicationService.recordCacheHit(responseTime);

      return {
        success: true,
        data: shows,
      };
    } catch (error) {
      // Record failed request performance
      const responseTime = Date.now() - startTime;
      requestDeduplicationService.recordCacheMiss(responseTime);
      
      debugError("❌ Error fetching shows for date:", error);
      return {
        success: false,
        data: [],
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }
}

export const scheduleApiService = new ScheduleApiService();
